import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';

import {SemanticLoader as Loader} from '../../../components/utility/InstrumentedLoader';
import {useFetchProjectJobsQuery} from '../../../generated/graphql';
import {Analytics} from '../../../services/analytics';
import {job as jobUrl} from '../../../util/urls';
import {usePublicQueueAccess} from '../hooks';
import {JobDrawer} from '../JobDrawer/JobDrawer';
import {WANDB_ENTITY_NAME, WANDB_JOBS_PROJECT_NAME} from '../utils';
import {getPublicJobInfo, JobInfo} from './jobUtil';
import * as S from './PublicJobsGettingStartedSection.styles';

export const PublicJobsGettingStartedSection: React.FC<{}> = () => {
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  const {hasPublicQueueAccess, isFeatureFlagLoading} = usePublicQueueAccess();
  const publicJobsQuery = useFetchProjectJobsQuery({
    variables: {
      entityName: WANDB_ENTITY_NAME,
      projectName: WANDB_JOBS_PROJECT_NAME,
    },
    skip: !hasPublicQueueAccess,
  });

  if (isFeatureFlagLoading || publicJobsQuery.loading) {
    return <Loader active name="jobs-page-landing-loader" />;
  }

  const publicJobEdges: any[] =
    publicJobsQuery.data?.project?.artifactType?.artifactCollections?.edges ??
    [];

  const publicJobs = getPublicJobInfo(
    publicJobEdges,
    WANDB_ENTITY_NAME,
    WANDB_JOBS_PROJECT_NAME
  );

  const onJobLaunch = (jobId: string) => {
    setSelectedJobId(jobId);
    Analytics.track('clicked launch public job', {
      jobId,
      location: 'zero jobs page',
    });
  };

  const closeDrawer = () => {
    setSelectedJobId(null);
  };

  if (publicJobs.length === 0) {
    return null;
  }

  return (
    <S.PublicJobsContainer>
      <S.SmallHeading>Get started with W&B public jobs</S.SmallHeading>
      <S.JobsContainer>
        {publicJobs.map(job => (
          <PublicJobCard
            key={job.id}
            job={job}
            onLaunch={() => onJobLaunch(job.id)}
          />
        ))}
      </S.JobsContainer>

      <JobDrawer
        open={selectedJobId != null}
        closeDrawer={closeDrawer}
        entityName={WANDB_ENTITY_NAME}
        projectName={WANDB_JOBS_PROJECT_NAME}
        defaultJobId={selectedJobId ?? undefined}
        defaultJobVersion="latest"
      />
    </S.PublicJobsContainer>
  );
};

interface PublicJobCardProps {
  job: JobInfo;
  onLaunch: () => void;
}

const PublicJobCard: React.FC<PublicJobCardProps> = ({job, onLaunch}) => {
  const history = useHistory();

  const handleCardClick = () => {
    const jobDetailUrl = jobUrl({
      entityName: job.entityName,
      projectName: job.projectName,
      jobId: job.id,
      jobTab: 'runs',
    });

    Analytics.track('clicked public job card', {
      jobId: job.id,
      location: 'zero jobs page',
    });

    history.push(jobDetailUrl);
  };

  return (
    <S.JobCard onClick={handleCardClick} style={{cursor: 'pointer'}}>
      <S.FlashIconContainer>
        <S.FlashIcon name="flash-bolt" />
      </S.FlashIconContainer>
      <S.JobContent>
        <S.JobName>{job.name}</S.JobName>
        <S.JobDescription>{job.description}</S.JobDescription>
      </S.JobContent>
      <div onClick={e => e.stopPropagation()}>
        <S.LaunchButton
          onClick={onLaunch}
          role="button"
          aria-label="Open launch drawer">
          <S.LaunchIcon name="play" />
        </S.LaunchButton>
      </div>
    </S.JobCard>
  );
};
